import { call, all, delay, put, select, takeLatest } from 'redux-saga/effects';
import { isAfter } from 'date-fns';

import history from '~/services/history';
import notification from '~/services/notification';
import teamService from '~/services/modules/team/team-service';
import productService from '~/store/modules/product/product-service';
import sponsoredService from './sponsored-service';
import { ISA_ERROR } from '~/constants';
import {
  GET_ALL_REQUEST,
  GET_BY_ID_REQUEST,
  SAVE_REQUEST,
  EDIT_ITEM,
  DELETE_ITEM,
  GET_PRODUCT_LIST_REQUEST
} from './sponsored-constants';
import * as sponsoredActions from './sponsored-actions';
import * as productActions from '../product/product-actions';
import axios from 'axios';
import { formatMessage } from '~/i18n/translate';

const INITIAL_VALUES = {
  description: '',
  pointsMultiplier: 0,
  dateRange: [new Date(), null],
  selectedProduct: null,
  selectedTeams: [],
  useTeams: false,
  sendNotifications: false
};

let cancel;
const CancelToken = axios.CancelToken;

export function* getAll() {
  try {
    yield put(sponsoredActions.setLoadingData(true));

    const { data } = yield call(sponsoredService.getAllSponsored);
    if (data) {
      yield put(
        sponsoredActions.getAllSuccess([
          ...data.map((item: any) => ({
            ...item,
            isExpired: isAfter(new Date(), item?.endDate)
          }))
        ])
      );
    }

    yield put(sponsoredActions.setLoadingData(false));
  } catch (error) {
    yield put(sponsoredActions.setLoadingData(false));
    yield put({ type: ISA_ERROR, error });
  }
}

export function* productRawList({ queryString }: any) {
  yield delay(200);
  const sponsored = yield select(state => state.sponsored);

  try {
    yield put(productActions.setLoadingData(true));
    const { data: products } = yield call(
      productService.getRawProducts,
      {
        skip: 0,
        limit: 100,
        queryString
      },
      {
        cancelToken: new CancelToken(function executor(c) {
          cancel = c;
        })
      }
    );

    yield put(
      sponsoredActions.getByIdSuccess({
        data: sponsored.form.data,
        selects: {
          ...sponsored.form.selects,
          products
        }
      })
    );
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  } finally {
    yield put(productActions.setLoadingData(false));
  }
}

export function* getById({ id }: any) {
  try {
    yield put(sponsoredActions.setLoadingData(true));

    const { data: teams } = yield call(teamService.getTeams);

    const { data: products } = yield call(
      productService.getRawProducts,
      {
        skip: 0,
        limit: 100,
        queryString: ''
      },
      {
        cancelToken: new CancelToken(function executor(c) {
          cancel = c;
        })
      }
    );

    if (!id) {
      yield put(
        sponsoredActions.getByIdSuccess({
          data: INITIAL_VALUES,
          selects: {
            products,
            teams
          }
        })
      );
    } else {
      const { data } = yield call(sponsoredService.getSponsored, id);
      if (data) {
        let selectedProduct;
        if (!!products.find((t: any) => t.externalCode === data.productExternalCode)) {
          selectedProduct = products.find((t: any) => t.externalCode === data.productExternalCode);
        } else {
          const response = yield call(
            productService.getRawProducts,
            {
              skip: 0,
              limit: 100,
              queryString: data.productExternalCode
            },
            {
              cancelToken: new CancelToken(function executor(c) {
                cancel = c;
              })
            }
          );
          selectedProduct = response.data.find((t: any) => t.externalCode === data.productExternalCode);
        }

        yield put(
          sponsoredActions.getByIdSuccess({
            data: {
              ...data,
              selectedProduct,
              selectedTeams: data?.teams?.map((s: any) => teams.find((t: any) => t._id === s)).filter(Boolean),
              dateRange: [new Date(data.startDate), new Date(data.endDate)],
              useTeams: data?.teams.length > 0
            },
            selects: {
              products,
              teams
            }
          })
        );
      }
    }
    yield put(sponsoredActions.setLoadingData(false));
  } catch (error) {
    yield put(sponsoredActions.setLoadingData(false));
    yield put({ type: ISA_ERROR, error });
  }
}

export function* save(data: any) {
  try {
    const { id, payload } = data;
    yield put(sponsoredActions.setLoadingSend(true));

    const { useTeams, sendNotifications } = payload;
    const sponsored = {
      description: payload?.description,
      productName: payload?.selectedProduct?.name,
      productExternalCode: payload?.selectedProduct?.externalCode,
      startDate: Date.parse(payload?.dateRange[0]),
      endDate: Date.parse(payload?.dateRange[1]),
      pointsMultiplier: payload?.pointsMultiplier,
      teams: useTeams ? payload?.selectedTeams.map((t: any) => t._id) : []
    };

    if (id) {
      yield call(sponsoredService.editSponsored, sendNotifications, {
        _id: id,
        ...sponsored
      });
    } else {
      yield call(sponsoredService.createSponsored, sendNotifications, sponsored);
    }
    yield call(history.push, `/engine/sponsored/`, {
      date: new Date().getTime()
    });
    yield put(sponsoredActions.setLoadingSend(false));
    yield call(
      notification.success,
      id ? formatMessage('sponsoredFormCreatedSuccess') : formatMessage('sponsoredFormUpdatedSuccess')
    );
  } catch (error) {
    yield put(sponsoredActions.setLoadingSend(false));
    yield put({ type: ISA_ERROR, error });
  }
}

export function* deleteItem({ id }: any) {
  try {
    const { isConfirmed } = yield call(
      notification.confirm,
      formatMessage('commonLabelExclude'),
      formatMessage('commonLabelExcludeItem')
    );
    if (isConfirmed) {
      yield put(sponsoredActions.setLoadingSend(true));
      yield call(sponsoredService.deleteSponsored, id);
      yield put(sponsoredActions.setLoadingSend(false));

      yield put(sponsoredActions.getAllRequest());
      yield call(notification.success, formatMessage('sponsoredFormExcludeSuccess'));
    }
  } catch (error) {
    yield put(sponsoredActions.setLoadingSend(false));
    yield put({ type: ISA_ERROR, error });
  }
}

export function* editItem({ id }: any) {
  yield call(history.push, `/engine/sponsored/edit/${id}`);
}

export default all([
  takeLatest(GET_ALL_REQUEST, getAll),
  takeLatest(GET_BY_ID_REQUEST, getById),
  takeLatest(SAVE_REQUEST, save),
  takeLatest(DELETE_ITEM, deleteItem),
  takeLatest(EDIT_ITEM, editItem),
  takeLatest(GET_PRODUCT_LIST_REQUEST, productRawList)
]);
